/**
 * Materialized booking row for the marketing dashboard's granular leads table
 * (one row per Calendly booking). Built daily by the analytics-service
 * (TRE-1457) by joining Calendly (sales_meeting), product_order (sale amounts),
 * Hyros (validation + attribution) and Active Campaign (tags / follow-up).
 *
 * The campaign aggregate table is derived from these rows joined to
 * meta_campaign_insight via `normalizedCampaignId`.
 */

/** Booking outcome, derived from sales_meeting status / callStatus. */
export enum MarketingLeadStatus {
  Scheduled = "scheduled",
  Showed = "showed",
  NoShow = "no_show",
  Canceled = "canceled",
  Resigned = "resigned",
}

export type MarketingLead = {
  id: string;
  /** Source sales_meeting id (one lead per meeting; idempotency key). */
  meetingId: string;
  email: string;
  /** Meeting start (ISO), drives the conversion window. */
  startTime: string;
  /** Sales rep / Calendly host display name. */
  assignedTo: string | null;

  // ── Campaign (Calendly UTM -> Meta) ────────────────────────────────────────
  utmCampaign: string | null;
  /** Meta-side campaign id (utm normalized 448 -> 446), join key to insights. */
  normalizedCampaignId: string | null;
  /** False when the UTM did not carry the expected suffix (broken tracking). */
  campaignMatched: boolean;

  // ── Qualification (Calendly form + status) ─────────────────────────────────
  status: MarketingLeadStatus;
  /** "Ile jesteś w stanie zainwestować" answer. */
  budgetAnswer: string | null;
  /** "Czy ten termin Ci odpowiada" answer. */
  terminOk: string | null;

  // ── Active Campaign enrichment ─────────────────────────────────────────────
  acTags: string[];
  acFollowUp: string | null;
  acNoReason: string | null;

  // ── Sale (product_order = source of truth) ─────────────────────────────────
  sale: boolean;
  product: string | null;
  contractValue: number | null;
  saleDate: string | null;

  // ── Hyros validation + attribution (TRE-1462) ──────────────────────────────
  originSource: string | null;
  lastSource: string | null;
  /** Whether Hyros also sees this sale in the 15-day window. */
  hyrosMatch: boolean | null;
  /** Hyros realValue minus product_order amount (PLN), null when no comparison. */
  hyrosDiscrepancy: number | null;

  /** Meeting happened and the 15-day window has not elapsed (recomputed daily). */
  conversionWindowOpen: boolean;
  syncedAt: string;
  createdAt: string;
  updatedAt: string | null;
};
